import type { TFunction } from "i18next";
import { Money } from "./common/money";
import { Utility } from "./bill-settings/bill-settings";
import {
	BillingTransactionHistory,
	BulkTransactionHistory,
	FriendRequestIcon,
	InstallmentTransactionHistory,
	OrderTransactionHistory,
	PayAsYouGoTransactionHistory,
	ServiceRequestTransactionHistory,
	VendingTransactionHistory,
} from "../assets/icons";

export type TransactionStatus = "In-Progress" | "Failed" | "Completed" | "Unknown" | "Refunded" | "Borrowed" | "Expired" | "Cancelled";
export type TransactionType = "Payment Hub" | "Cash Payment";

export const TRANSACTION_STATUS = {
	UNKNOWN: 0,
	IN_PROGRESS: 1,
	COMPLETED: 2,
	FAILED: 3,
	REFUNDED: 4,
	BORROWED: 5,
	EXPIRED: 6,
	CANCELLED: 7,
	QUEUED_FOR_MANUAL_REVIEW: 8,
	AEDC_CONFIRMED: 101,
	AEDC_PENDING: 102,
	AEDC_FAILED: 103,
	AEDC_DECLINED: 104,
	AEDC_EXPIRED: 105,
} as const;

export type TransactionStatusValue = (typeof TRANSACTION_STATUS)[keyof typeof TRANSACTION_STATUS];

//Transaction interface - minimal set of properties; transaction histort
export interface TransactionItem {
	id: string;
	reference: string;
	externalReference: string;
	transactionType?: number; // Old property name
	type: number; // New property name
	status: TransactionStatusValue;
	createdAt: string;
	updatedAt: string | Date; // Support both formats
	amount: Money;
	category: TransactionCategory;
	transactionPaymentType?: number;
	// Status of the latest refund attempt (Refunded / InProgress / QueuedForManualReview only); null when never refunded
	refundStatus?: TransactionStatusValue | null;
}

// Refund details, looked up by the original transaction's reference
export interface RefundTransaction {
	payment?: string | null;
	refunded: string;
	status: TransactionStatusValue;
	refundedTo: "Card" | "Wallet";
	amount: Money;
	reason: string;
	numOfAttempt: number;
	paymentReference: string;
	walletBalance?: number | null;
	walletBankName?: string | null;
}
// Main Transaction interface - combining both old and new properties
export interface Transaction {
	id: string;
	reference: string;
	externalReference: string;
	transactionType?: number; // Old property name
	type: number; // New property name
	status: TransactionStatusValue;
	createdAt: string;
	updatedAt: string | Date; // Support both formats
	amount: Money;
	user: User;
	customerEnergyAccount?: CustomerEnergyAccount;
	billingDetails?: BillingDetails;
	vendingDetails?: VendingDetails;
	orderDetails?: OrderDetails;
	installmentDetails?: InstallmentDetails;
	payAsYouGoDetails?: PayAsYouGoDetails;
	bulkPurchaseDetails?: BulkPurchaseDetails;
	serviceRequestDetails?: ServiceRequestDetails;
	transactionPaymentType?: number;
	friendPaymentRequest?: FriendPaymentRequest;
}

export interface FriendPaymentRequest {
	id: string;
	recipientEmail: string;
	recipientPhoneNumber?: string;
	note?: string;
	requestLink: string;
	expiresAt: string;
}

// BillingDetails - Extended from API response
export interface BillingDetails {
	id: string;
	customerId: string;
	accountNumber: string;
	accountName: string;
	meterNumber: string;
	address: string;
	reference: string;
	tariffDescription: string;
	amount: Money;
	previousOutstandingBalance: Money;
	isBillingNotified: boolean;
	utility: Utility;
	createdAt: string;
	updatedAt: string;
	notifyTimeline?: BillNotifyTimelineItem[];

	// Old properties for backward compatibility
	customerName?: string;
	channel?: string;
	paymentStatus?: string;
	vendingStatus?: "Success" | "Failed" | "Pending";
	postpaidStatus?: "Success" | "Failed" | "Pending";
	dateTime?: string;
	vendingToken?: VendingToken;
	district?: string;
}

// Old BillTransaction type alias for backward compatibility
export type BillTransaction = BillingDetails;

// CustomerEnergyAccount from old interface
export interface CustomerEnergyAccount {
	id: string;
	isDeleted: boolean;
	userId: string;
	consumerType: number;
	meterNumber: string;
	accountNumber: string;
	meterAddress: string;
	utilityId: string;
	utility?: Utility;
	pendingStatus: number;
	createdAt: string;
	updatedAt: string;
	teamId?: string;
}

// Vending workflow step from API response (vendingDetails.historySteps[])
export interface VendingHistoryStep {
	typeOfEvent: number; // VendingStepType enum value (0-5)
	subTypeOfEvent: number; // VendingStepStatus enum value (0-4)
	vendingStepName: string; // User-friendly step name
	vendingStepStatusName: string; // User-friendly status name
	timestamp: string; // ISO timestamp
	timeFormated: string; // Pre-formatted timestamp (dd MMM yyyy, hh:mm a)
}

export interface VendingDetails {
	id: string;
	accountNumber: string;
	accountName: string;
	meterNumber: string;
	address: string;
	utility: Utility;
	vendingTokens?: VendingToken[];
	vendingToken: VendingToken;
	amount?: Money;
	paymentStatus?: number;
	transactionReference?: string;
	historySteps?: VendingHistoryStep[]; // Vending workflow progress steps
	totalTokenCount?: number;
	failedTokenCount?: number;
	tokenTimeline?: VendingTokenTimelineItem[];
	createdAt: string;
	updatedAt: string;
	district: string;
}

export interface VendingToken {
	id: string;
	token: string;
	vendingStatus: number;
	totalKwh: number;
	createdAt: string;
	updatedAt: string;
	// Meter Asset Pooling Refund Token
	mapRefundToken?: string;
	vendingRemoteTokenLoadId?: string;
}

// One vending-token generation attempt (vendingDetails.tokenTimeline[]), ordered by createdAt asc
export interface VendingTokenTimelineItem {
	vendingTransactionId: string;
	createdAt: string;
	updatedAt: string;
	status: number; // VendingStatus
	token?: string;
	mapRefundToken?: string;
	totalKwh: number;
	vendingRemoteTokenLoadId?: string;
	error?: string;
	requestSource: number; // RequestSource
	requestedByUserId?: string;
	requestedByName?: string;
}

// One bill-notify attempt (billingDetails.notifyTimeline[]), ordered by createdAt asc
export interface BillNotifyTimelineItem {
	id: string;
	createdAt: string;
	isSuccess: boolean;
	errorMessage?: string;
	requestSource: number; // RequestSource
	requestedByUserId?: string;
	requestedByName?: string;
}

export const REQUEST_SOURCE = {
	SYSTEM: 0,
	CUSTOMER: 1,
	UTILITY_ADMIN: 2,
} as const;

export const RequestSourceMap: Record<number, string> = {
	0: "System",
	1: "Customer",
	2: "Utility Admin",
};

export interface OrderDetails {
	orderTransactionType: number;
	orderId: string;
	customerId: string;
	recurringContextId: string;
	order: Order;
}

export interface Order {
	id: string;
	createdAt: string;
	productDetails: ProductDetails;
	statusDetails: StatusDetails;
	deliveryInformation: DeliveryInformation;
	costOverview: CostOverview;
}

export interface ProductDetails {
	id: string;
	productName: string;
	productCapacity: number;
	activeDuration: number;
	packageDescription: string;
	batteryType: string;
	inverterType: string;
	warranty: string;
	batteryVoltage: string;
	installationCostForBungalow: Money;
	installationCostForFloorForStoryBuilding: Money;
	images: ImageDto[];
	categories: CategoryDto[];
}

export interface ImageDto {
	id: string;
	imageUrl: string;
}
export interface CategoryDto {
	id: string;
	name: string;
}

export interface StatusDetails {
	status: number;
	updatedAt: string;
}

export interface DeliveryInformation {
	id: string;
	address: string;
	additionalInformation?: string;
}

export interface CostOverview {
	productPrice: Money;
	installmentPrice: Money;
	insurancePrice: Money;
	tax: Money;
	shipping: Money;
	totalAmount: Money;
}

export enum InstallmentPaymentType {
	AutomaticBillPayment = 1,
	CustomerBillPayment = 2,
	AdminManuallyPayment = 3,
	CustomPayment = 4,
}

export const InstallmentPaymentTypeMap: Record<InstallmentPaymentType, string> = {
	[InstallmentPaymentType.AutomaticBillPayment]: "Automatic Bill Payment",
	[InstallmentPaymentType.CustomerBillPayment]: "Customer Bill Payment",
	[InstallmentPaymentType.AdminManuallyPayment]: "Admin Manual Payment",
	[InstallmentPaymentType.CustomPayment]: "Custom Payment",
};

export const getInstallmentPaymentTypeDisplay = (type: number): string => {
	return InstallmentPaymentTypeMap[type as InstallmentPaymentType] ?? "Unknown";
};

export interface InstallmentDetails {
	paymentType: number;
	repaymentMonthlyLoanProcessId: string;
}

export interface PayAsYouGoDetails {
	payAsYouGoSubscriptionId: string;
	subscriptionBillId: string;
}

export interface BulkPurchaseDetails {
	id: string;
	isDeleted: boolean;
	vendingDetailsList: VendingDetails[];
	billingDetailsList: BillingDetails[];
	utilityId: string;
	customerId: string;
	paymentStatus: number;
	transactionReference: string;
	amount: Money;
	createdAt: string;
	updatedAt: string;
}

export interface ServiceRequestDetails {
	serviceRequestDetailsid: string;
	name: string;
	amount: Money;
	transactionReference: string;
	address: string;
	date: string;
	paymentChannel: string;
}

interface CustomerDetails {
	countryCode: string;
	customerType: string;
}

interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	userType: string;
	created: string;
	lastLogin: string;
	phoneNumber: string;
	hasPin: boolean;
	customerDetails: CustomerDetails;
}

export interface TransactionParams {
	Page: number;
	PageSize: number;
	Reference?: string;
	UtilityId?: string;
	Search?: string;
	TransactionStatus?: string;
	VendingStatus?: string;
	HasMapRefundToken?: boolean;
	PaymentStatus?: string;
	BillingStatus?: boolean;
	From?: string;
	To?: string;
	DateFrom?: string;
	DateTo?: string;
}

export interface CustomerTransactionParams {
	page: number;
	pageSize: number;
	searchQuery?: string;
	category?: number;
	status?: number;
	dateFrom?: string;
	dateTo?: string;
}

export const TransactionStatusMap: Record<number, TransactionStatus> = {
	0: "Unknown",
	1: "In-Progress",
	2: "Completed",
	3: "Failed",
	4: "Refunded",
	5: "Borrowed",
	6: "Expired",
	7: "Cancelled",
};

// Vending status has its own enum on the backend (VendingStatus): it is NOT the
// same scale as TransactionStatusMap above (where 4 = "Refunded"). Keep a
// dedicated map so vendingStatus 4 renders as "Failed with Refund".
export type VendingStatusLabel = "Unknown" | "In-Progress" | "Completed" | "Failed" | "Failed with Refund";

export const VendingStatusMap: Record<number, VendingStatusLabel> = {
	0: "Unknown",
	1: "In-Progress",
	2: "Completed",
	3: "Failed",
	4: "Failed with Refund",
};

// CSS-safe class key for the status-indicator dot (e.g. "failed-with-refund").
export const vendingStatusClassName = (status: number | undefined | null): string =>
	(VendingStatusMap[status ?? 0] ?? "unknown").toLowerCase().replace(/\s+/g, "-");

export const TransactionPaymentTypeMap: Record<number, string> = {
	0: "Unknown",
	1: "Card",
	2: "Cash",
	3: "Transfer",
	4: "Wallet",
	5: "OneTimeWallet",
	6: "FriendRequest",
};

export const TransactionChannelMap: Record<number, TransactionType> = {
	1: "Payment Hub",
	2: "Cash Payment",
};

// Helper function to get user-friendly transaction status
export const getUserFriendlyTransactionStatus = (status: number): TransactionStatus => {
	return TransactionStatusMap[status] || "Unknown";
};

// Helper function to get user-friendly transaction type
export const getUserFriendlyTransactionType = (type: number): TransactionType => {
	return TransactionChannelMap[type] || "Payment Hub";
};

export enum TransactionCategory {
	Unknown = 0,
	VendingTransaction = 1,
	BillingTransaction = 2,
	OrderTransactions = 3,
	InstallmentTransaction = 4,
	PayAsYouGoTransaction = 5,
	BulkPurchaseTransaction = 6,
	ServiceRequesTransaction = 7,
}
export interface TitleAndCategory {
	title: string;
	category: TransactionCategory;
	icon?: React.ReactNode;
}
// Helper function to get transaction title based on details
export const getTransactionTitleAndCategory = (transaction: TransactionItem, t: TFunction): TitleAndCategory => {
	if (transaction.transactionPaymentType === 6) {
		return { title: t("history.requestForAFriend"), category: transaction.category, icon: <FriendRequestIcon /> };
	}
	if (transaction.category == TransactionCategory.VendingTransaction) {
		return { title: t("history.vendingTransactions"), category: TransactionCategory.VendingTransaction, icon: <VendingTransactionHistory /> };
	} else if (transaction.category == TransactionCategory.BillingTransaction) {
		return {
			title: t("history.billingTransactions"),
			category: TransactionCategory.BillingTransaction,
			icon: <BillingTransactionHistory />,
		};
	} else if (transaction.category == TransactionCategory.OrderTransactions) {
		return { title: t("history.orderTransactions"), category: TransactionCategory.OrderTransactions, icon: <OrderTransactionHistory /> };
	} else if (transaction.category == TransactionCategory.InstallmentTransaction) {
		return { title: t("history.installmentTransactions"), category: TransactionCategory.InstallmentTransaction, icon: <InstallmentTransactionHistory /> };
	} else if (transaction.category == TransactionCategory.PayAsYouGoTransaction) {
		return { title: t("history.payAsYouGoTransactions"), category: TransactionCategory.PayAsYouGoTransaction, icon: <PayAsYouGoTransactionHistory /> };
	} else if (transaction.category == TransactionCategory.BulkPurchaseTransaction) {
		return { title: t("history.bulkPurchase"), category: TransactionCategory.BulkPurchaseTransaction, icon: <BulkTransactionHistory /> };
	} else if (transaction.category == TransactionCategory.ServiceRequesTransaction) {
		return { title: t("common.labels.serviceRequest"), category: TransactionCategory.ServiceRequesTransaction, icon: <ServiceRequestTransactionHistory /> };
	}
	return { title: t("history.unknownTransaction"), category: TransactionCategory.Unknown };
};

// Helper function to format transaction status for display
export const getTransactionStatusForDisplay = (
	status: number
): "successful" | "pending" | "aedcPending" | "failed" | "refunded" | "expired" | "cancelled" | "confirmed" | "declined" | "unknown" => {
	switch (status) {
		case TRANSACTION_STATUS.COMPLETED:
			return "successful";
		case TRANSACTION_STATUS.IN_PROGRESS:
			return "pending";
		case TRANSACTION_STATUS.FAILED:
		case TRANSACTION_STATUS.AEDC_FAILED:
			return "failed";
		case TRANSACTION_STATUS.REFUNDED:
			return "refunded";
		case TRANSACTION_STATUS.EXPIRED:
		case TRANSACTION_STATUS.AEDC_EXPIRED:
			return "expired";
		case TRANSACTION_STATUS.CANCELLED:
			return "cancelled";
		case TRANSACTION_STATUS.AEDC_CONFIRMED:
			return "confirmed";
		case TRANSACTION_STATUS.AEDC_PENDING:
			return "aedcPending";
		case TRANSACTION_STATUS.AEDC_DECLINED:
			return "declined";
		default:
			return "unknown";
	}
};

// Refund status of a list item for display; undefined means show the transaction's own status
export const getRefundStatusForDisplay = (refundStatus?: number | null): "refunded" | "refundInProgress" | "refundQueued" | undefined => {
	switch (refundStatus) {
		case TRANSACTION_STATUS.REFUNDED:
			return "refunded";
		case TRANSACTION_STATUS.IN_PROGRESS:
			return "refundInProgress";
		case TRANSACTION_STATUS.QUEUED_FOR_MANUAL_REVIEW:
			return "refundQueued";
		default:
			return undefined;
	}
};

export interface TransactionUtilityAdminParams {
	Page: number;
	PageSize: number;
	Search?: string;
	TransactionStatus?: string;
	VendingStatus?: string;
	PaymentStatus?: string;
	BillingStatus?: boolean;
	TransactionType?: string;
	From?: string;
	To?: string;
	DateFrom?: string;
	DateTo?: string;
}

export interface TransactionReference {
	Reference: string;
}

// Dashboard Summary types
export interface DashboardMetric<T> {
	value: T;
	percentageChange: number;
	/** This metric's share of its total (e.g. successful RTL / all RTL requests); null for metrics without one. */
	sharePercentage?: number | null;
}

export interface UtilityDashboardSummary {
	totalTransactionsVolume: DashboardMetric<number>;
	prepaidTransactionsVolume: DashboardMetric<number>;
	postpaidTransactionsVolume: DashboardMetric<number>;
	totalSales: DashboardMetric<number>;
	usersRegisteredToday: DashboardMetric<number>;
	uniqueUserLogins: DashboardMetric<number>;
	totalRegisteredUsers: DashboardMetric<number>;
	transactingCustomers: DashboardMetric<number>;
	totalTransactionCount: DashboardMetric<number>;
	totalSignUp: DashboardMetric<number>;
	successfulTransactions: DashboardMetric<number>;
	failedTransactions: DashboardMetric<number>;
	totalServiceRequestsSubmitted: DashboardMetric<number>;
	totalRTLRequest: DashboardMetric<number>;
	successfulRTL: DashboardMetric<number>;
	failedRTL: DashboardMetric<number>;
}

export interface UtilityDashboardSummaryParams {
	DateFrom?: Date;
	DateTo?: Date;
}

export interface DistrictRevenueItem {
	district: string;
	amount: number;
	percentage: number;
}

export interface DistrictRevenueData {
	totalAmount: number;
	districts: DistrictRevenueItem[];
}

export interface DistrictRevenueParams {
	DateFrom?: Date;
	DateTo?: Date;
}

export interface AggregatedTransactionsRequest {
	DateFrom?: Date;
	DateTo?: Date;
	AggregationType?: AggregationType;
	PurposeType?: TransactionPurposeType;
}

export enum AggregationType {
	HOUR = 0,
	DAY = 1,
	MONTH = 2,
}

export enum TransactionPurposeType {
	Billing = 0,
	Vending = 1,
}

export function getTransactionPurposeTypeString(type: TransactionPurposeType) {
	switch (type) {
		case TransactionPurposeType.Billing:
			return "Postpaid";
		case TransactionPurposeType.Vending:
			return "Prepaid";
	}
}
